import type { ApiSchemas } from '@rental/contracts/client'

type Body = ApiSchemas['SecondDriverRequestInput']

/** A new person typed at the counter as the second driver. */
export interface DriverDraft {
  firstName: string
  lastName: string
  birthDate: string
  nationality: string
  phone: string
}

/** Their licence as typed; nothing is sent while its number is empty. */
export interface LicenceDraft {
  number: string
  country: string
  category: string
  firstIssued: string
  expires: string
  hasIdp: boolean
}

export const EMPTY_DRIVER: DriverDraft = {
  firstName: '',
  lastName: '',
  birthDate: '',
  nationality: '',
  phone: '',
}

export const EMPTY_LICENCE: LicenceDraft = {
  number: '',
  country: '',
  category: '',
  firstIssued: '',
  expires: '',
  hasIdp: false,
}

/** What the second-driver form still lacks, said beside its button, in the page's order. */
export type SecondDriverReason =
  | 'customer'
  | 'firstName'
  | 'lastName'
  | 'nationality'
  | 'phone'
  | 'licenceNumber'
  | 'licenceCountry'
  | 'overrideReason'

const E164 = /^\+[1-9][0-9]{6,14}$/
const COUNTRY = /^[A-Za-z]{2}$/

interface FormState {
  mode: 'existing' | 'new'
  /** The customer chosen from the register, in `existing` mode. */
  chosen: string | null
  person: DriverDraft
  licence?: LicenceDraft
  /** The rules said no and the Owner is giving a reason. */
  overriding: boolean
  overrideReason: string
}

const compact = (phone: string) => phone.replace(/[\s().-]/g, '')

/**
 * Q-162: what stops the form being sent. A licence is optional (someone on
 * file may have one already; a typed one is judged on the server), but once
 * its number is typed it needs its country, as the API's own rule.
 */
export function secondDriverReasons(state: FormState): SecondDriverReason[] {
  const reasons: SecondDriverReason[] = []
  if (state.mode === 'existing') {
    if (!state.chosen) reasons.push('customer')
  } else {
    const { person } = state
    if (person.firstName.trim() === '') reasons.push('firstName')
    if (person.lastName.trim() === '') reasons.push('lastName')
    if (person.nationality.trim() !== '' && !COUNTRY.test(person.nationality.trim())) {
      reasons.push('nationality')
    }
    const phone = compact(person.phone)
    if (phone !== '' && !E164.test(phone)) reasons.push('phone')
  }
  const licence = state.licence
  if (licence && licence.number.trim() !== '') {
    if (licence.number.trim().length < 3) reasons.push('licenceNumber')
    if (!COUNTRY.test(licence.country.trim())) reasons.push('licenceCountry')
  }
  if (state.overriding && state.overrideReason.trim().length < 3) reasons.push('overrideReason')
  return reasons
}

/** The request, once nothing is missing; null otherwise. */
export function secondDriverBody(
  state: FormState & {
    expectedVersion: number
    licence: LicenceDraft
    /** The phone as the API keeps it (E.164), from the app's one helper. */
    phone: (typed: string) => string
  },
): Body | null {
  if (secondDriverReasons(state).length > 0) return null
  const { person, licence } = state
  const customer: Body['customer'] =
    state.mode === 'existing' && state.chosen
      ? { id: state.chosen }
      : {
          firstName: person.firstName.trim(),
          lastName: person.lastName.trim(),
          ...(person.birthDate ? { birthDate: person.birthDate } : {}),
          ...(person.nationality.trim()
            ? { nationality: person.nationality.trim().toUpperCase() }
            : {}),
          ...(compact(person.phone) ? { phone: state.phone(person.phone) } : {}),
        }
  return {
    expectedVersion: state.expectedVersion,
    customer,
    ...(licence.number.trim()
      ? {
          licence: {
            number: licence.number.trim(),
            issuingCountry: licence.country.trim().toUpperCase(),
            categories: [
              {
                code: licence.category.trim().toUpperCase() || 'B',
                ...(licence.firstIssued ? { firstIssuedOn: licence.firstIssued } : {}),
              },
            ],
            ...(licence.expires ? { expiresOn: licence.expires } : {}),
            hasIdp: licence.hasIdp,
          },
        }
      : {}),
    ...(state.overriding && state.overrideReason.trim()
      ? { overrideReason: state.overrideReason.trim() }
      : {}),
  }
}
